import { Box, Typography, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatVolume } from '../utils/format';
import type { ConvictionTimePoint } from '../api/useConvictionTimeseries';

export interface RowConvictionCircle {
    group: string;
    label: string;
    color: string;
    rawImplied: number; // ring fill, 0..1
    rank: 'primary' | 'secondary';
    point: ConvictionTimePoint;
    resolved?: boolean;
    correct?: boolean;
}

interface ConvictionCirclesProps {
    circles: RowConvictionCircle[];
}

const PRIMARY_D = 26;
const SECONDARY_D = 20;
const RING_W = 3;

// Swap to a green here (e.g. '#10b981') if the check should read as a win.
const RESOLVED_GLYPH_COLOR = '#9e9e9e';

function ResolvedGlyph({ d, correct }: { d: number; correct: boolean }) {
    const w = Math.max(2, d * 0.1);
    if (correct) {
        return (
            <path
                d={`M ${0.3 * d} ${0.52 * d} L ${0.44 * d} ${0.65 * d} L ${0.71 * d} ${0.35 * d}`}
                fill="none"
                stroke={RESOLVED_GLYPH_COLOR}
                strokeWidth={w}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        );
    }
    return (
        <>
            <line
                x1={0.35 * d}
                y1={0.35 * d}
                x2={0.65 * d}
                y2={0.65 * d}
                stroke={RESOLVED_GLYPH_COLOR}
                strokeWidth={w}
                strokeLinecap="round"
            />
            <line
                x1={0.65 * d}
                y1={0.35 * d}
                x2={0.35 * d}
                y2={0.65 * d}
                stroke={RESOLVED_GLYPH_COLOR}
                strokeWidth={w}
                strokeLinecap="round"
            />
        </>
    );
}

function Donut({ circle }: { circle: RowConvictionCircle }) {
    const d = circle.rank === 'primary' ? PRIMARY_D : SECONDARY_D;
    const r = (d - RING_W) / 2;
    const c = 2 * Math.PI * r;
    const arc = Math.max(0, Math.min(1, circle.rawImplied)) * c;
    const resolved = !!circle.resolved;
    return (
        <svg width={d} height={d} style={{ display: 'block' }}>
            <circle
                cx={d / 2}
                cy={d / 2}
                r={r}
                fill="none"
                stroke={alpha(circle.color, 0.2)}
                strokeWidth={RING_W}
            />
            <circle
                cx={d / 2}
                cy={d / 2}
                r={r}
                fill="none"
                stroke={resolved ? alpha(circle.color, 0.35) : circle.color}
                strokeWidth={RING_W}
                strokeDasharray={`${arc} ${c - arc}`}
                strokeLinecap="round"
                transform={`rotate(-90 ${d / 2} ${d / 2})`}
            />
            {resolved ? (
                <ResolvedGlyph d={d} correct={!!circle.correct} />
            ) : (
                <circle
                    cx={d / 2}
                    cy={d / 2}
                    r={r - RING_W}
                    fill={alpha(
                        circle.color,
                        circle.rank === 'primary' ? 0.9 : 0.55
                    )}
                />
            )}
        </svg>
    );
}

function TooltipBody({ circle }: { circle: RowConvictionCircle }) {
    const p = circle.point;
    const rows: [string, string][] = [
        ['Implied', `${(circle.rawImplied * 100).toFixed(0)}%`],
        [
            'Net capital',
            `${p.net_capital >= 0 ? '+' : '-'}${formatVolume(Math.abs(p.net_capital))}`,
        ],
        [
            'Capital Y/N',
            `${formatVolume(p.capital_usd_yes)} / ${formatVolume(p.capital_usd_no)}`,
        ],
        ['Traders Y/N', `${p.n_traders_yes} / ${p.n_traders_no}`],
    ];
    if (circle.resolved) {
        rows.unshift(['Result', circle.correct ? 'Correct' : 'Wrong']);
    }
    return (
        <Box sx={{ py: 0.25 }}>
            <Typography
                sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: circle.color,
                    mb: 0.5,
                    whiteSpace: 'nowrap',
                }}
            >
                {circle.label}
                {p.direction ? ` \u00b7 ${p.direction}` : ''}
            </Typography>
            {rows.map(([k, v]) => (
                <Box
                    key={k}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1.5,
                    }}
                >
                    <Typography
                        sx={{ fontSize: '10px', color: 'text.secondary' }}
                    >
                        {k}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '10px',
                            fontFamily: 'monospace',
                            color: 'text.primary',
                        }}
                    >
                        {v}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

export function ConvictionCircles({ circles }: ConvictionCirclesProps) {
    if (circles.length === 0) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexShrink: 0,
            }}
        >
            {circles.map(circle => (
                <Tooltip
                    key={`${circle.group}-${circle.rank}`}
                    title={<TooltipBody circle={circle} />}
                    placement="top"
                    arrow
                    // Non-interactive: moving the cursor off the circle toward the
                    // tooltip dismisses it (the tooltip never captures the pointer).
                    disableInteractive
                    enterDelay={0}
                    leaveDelay={0}
                    slotProps={{
                        tooltip: {
                            sx: {
                                bgcolor: 'background.paper',
                                color: 'text.primary',
                                border: 1,
                                borderColor: 'divider',
                                boxShadow: 3,
                                borderRadius: '6px',
                                px: 1,
                                py: 0.75,
                                maxWidth: 'none',
                            },
                        },
                        arrow: { sx: { color: 'background.paper' } },
                    }}
                >
                    <Box sx={{ display: 'flex', cursor: 'default' }}>
                        <Donut circle={circle} />
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
}
